import {defineStore} from 'pinia';
import redirect_error_page from "@/util/redirect_error_page"
import { IHomeworkQuestionSet, IHomework } from "@/consts/interfaces";
import { useVersionStore } from './version_store';
import apiRequest from '@/util/api-request';
import { AxiosRequestConfig } from 'axios';


interface IHomeworkState {
    current_homeworks: Array<IHomework>;
    expired_homeworks: Array<IHomework>;
    homework_question_sets: Array<IHomeworkQuestionSet>;
    is_completed: boolean;
    is_current: boolean;
    homework_accordion: {[id: number] : boolean};
    homework_animation_id: number;
    homework_animation_ids: {[id: number] : number};
    is_homework: boolean;
    learning_homework_status: number;
    is_scroll: boolean;
}

export const useHomeworkStore = defineStore('homeworkStore', {
    // state
    state: (): IHomeworkState => {
        return {
            current_homeworks: [],
            expired_homeworks: [],
            homework_question_sets: [],
            is_completed: false,
            is_current: true,
            homework_accordion: {},
            homework_animation_id: 0,  // save current learning homework_id
            homework_animation_ids: {},  // save multiple homework_id for multiple animation
            is_homework: false,
            learning_homework_status: 0,
            is_scroll: false,  // track scrolling for multiple animation stamp
        }
    },
    // getters
    getters: {
        currentHomeworks: (state) => {
            return state.current_homeworks
        },
        expiredHomeworks: (state) => {
            return state.expired_homeworks
        },
        homeworkQuestionSets: (state) => {
            return state.homework_question_sets
        },
        isHomeworkCompleted: (state) => {
            return state.is_completed
        },
        isHomeworkCurrent: (state) => {
            return state.is_current
        },
        homeworkAccordion: (state) => {
            return state.homework_accordion
        },
        homeworkAnimationId: (state) => {
            return state.homework_animation_id
        },
        homeworkAnimationIds: (state) => {
            return state.homework_animation_ids
        },
        isHomework: (state) => {
            return state.is_homework
        },
        learningHomeworkStatus: (state) => {
            return state.learning_homework_status
        },
        isScrollIntoView: (state) => {
            return state.is_scroll
        }  
    },
    // actions
    actions: {
        async fetchCurrentHomework() {
            const versionStore = useVersionStore()
            try {
                const config: AxiosRequestConfig = {
                    method: 'get',
                    url: '/student/api/homework/current_homework',
                };
                const response = await apiRequest(config);
                this.current_homeworks = response.data.homeworks
                versionStore.saveAppVersion(response.headers["x-app-version"])
            } catch (error) {
                redirect_error_page(error)
            }
        },
        async fetchExpiredHomework() {
            const versionStore = useVersionStore()
            try {
                const config: AxiosRequestConfig = {
                    method: 'get',
                    url: '/student/api/homework/expired_homework',
                };
                const response = await apiRequest(config);
                this.expired_homeworks = response.data.homeworks
                versionStore.saveAppVersion(response.headers["x-app-version"])
            } catch (error) {
                redirect_error_page(error)
            }
        },
        async fetchHomeworkQuestionSet() {
            const versionStore = useVersionStore()
            try {
                const config: AxiosRequestConfig = {
                    method: 'get',
                    url: '/student/api/work_progress/homework',
                };
                const response = await apiRequest(config);
                this.homework_question_sets = response.data.homework_question_sets
                versionStore.saveAppVersion(response.headers["x-app-version"])
            } catch (error) {
                redirect_error_page(error)
            }
        },
        saveCurrentHomework(payload: Array<IHomework>) {
            this.current_homeworks = payload
        },
        saveExpiredHomework(payload: Array<IHomework>) {
            this.expired_homeworks = payload
        },
        saveHomeworkQuestionSet(payload: Array<IHomeworkQuestionSet>) {
            this.homework_question_sets = payload
        },
        saveIsHomeworkCompleted(payload: boolean) {
            this.is_completed = payload
        },
        saveIsHomeworkCurrent(payload: boolean) {
            this.is_current = payload
        },
        saveHomeworkAccordion(payload: {id: number, value: boolean}) {
            this.homework_accordion[payload.id] = payload.value
        },
        saveHomeworkAnimationId(payload: number) {
            this.homework_animation_id = payload
        },
        saveHomeworkAnimationIds(payload: {[id: number] : number}) {
            this.homework_animation_ids = payload
        },
        saveIsHomework(payload: boolean) {
            this.is_homework = payload
        },
        saveLearningHomeworkStatus(payload: number) {
            this.learning_homework_status = payload
        },
        deleteHomeworkAnimationId(payload: number) {
            delete this.homework_animation_ids[payload]
        },
        saveIsScrollIntoView(payload: boolean) {
            this.is_scroll = payload
        }
    }
})